import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SEO from '../components/SEO';
import SocialJuiceWall from '../components/SocialJuiceWall';
import LeadMagnetModal from '../components/LeadMagnetModal';
import EstimateCalculator from '../components/EstimateCalculator';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight, FiX } = FiIcons;

export default function Home() {
  const [isLeadMagnetOpen, setIsLeadMagnetOpen] = useState(false);
  const [isCalcOpen, setIsCalcOpen] = useState(false);

  const perryImage = "https://media-manager-c.questera.ai/greta-media/4320f8aaa9ed428bff142ca67321b3bfce2c39771503d4270b0b625b52f5e6960d1f85712829807a2ee1ac58fcf9446b/images/aW1hZ2UvcG5n/c724ae193239e8cd8dadf828fd04befa.png";
  const heroPhoto = "https://media-manager-c.questera.ai/greta-media/4320f8aaa9ed428bff142ca67321b3bfce2c39771503d4270b0b625b52f5e6960d1f85712829807a2ee1ac58fcf9446b/images/aW1hZ2UvanBlZw==/c0057a035a252f75634e20b124d696aa.jpg";

  return (
    <>
      <SEO 
        title="Social Media Marketing for Pet Care Businesses | Otso Co" 
        description="Otso Co helps pet care businesses grow on social media and attract more clients. Done-for-you social media management and 1:1 consulting." 
        path="/" 
      />

      {/* Hero Section */}
      <section className="flex flex-col md:flex-row min-h-[600px] overflow-hidden">
        <div className="w-full h-[300px] md:h-auto md:w-[45%] md:order-2">
          <img 
            src={heroPhoto} 
            alt="Perry Dasilva and Choco" 
            className="w-full h-full object-cover object-top"
          />
        </div>
        
        <div className="w-full md:w-[55%] bg-brand-primaryDark flex items-center md:order-1 px-6 py-16 md:py-24 md:pl-[60px]">
          <div className="max-w-2xl mx-auto md:mx-0 text-center md:text-left">
            <h1 className="text-white text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              Get More Clients for Your Pet Business <br />
              <span className="text-brand-mauve">Without Doing It All Yourself.</span>
            </h1>
            <p className="text-white text-lg md:text-xl opacity-90 mb-10 leading-relaxed max-w-xl">
              I help dog walkers, groomers, trainers, and pet care businesses show up consistently on social media and turn followers into paying clients.
            </p>
            
            <div className="flex flex-col items-center md:items-start space-y-4">
              <Link 
                to="/services/social-media-management"
                className="w-full sm:w-auto bg-brand-mediumPurple hover:bg-brand-mauve text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-lg text-center flex items-center justify-center gap-2 group"
              >
                See Plans + Pricing <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link 
                to="/pet-content-planner"
                className="text-white/70 hover:text-brand-blush text-sm transition-colors flex items-center gap-1 group"
              >
                Not ready to book yet? <span className="underline underline-offset-4 group-hover:text-white transition-colors">Get a free weekly content plan →</span>
              </Link>
            </div>

            <div className="mt-12 flex justify-center md:justify-start">
              <iframe id="socialjuice-sm-otso-co-5402" src="https://embed.socialjuice.io/sm/7473?s=otso-co" allowFullScreen allowTransparency="true" frameBorder="0" scrolling="no" width="370px" height="80px" title="SocialJuice Trust Badge" className="max-w-full"></iframe>
            </div>
          </div>
        </div>
      </section>

      {/* Lead Magnet Modal */}
      <LeadMagnetModal isOpen={isLeadMagnetOpen} onClose={() => setIsLeadMagnetOpen(false)} />

      {/* Estimate Calculator Modal (Kept for other potential triggers) */}
      <AnimatePresence>
        {isCalcOpen && (
          <motion.div 
            initial={{ opacity: 0 }} 
            animate={{ opacity: 1 }} 
            exit={{ opacity: 0 }} 
            className="fixed inset-0 z-[200] flex items-end md:items-center justify-center p-0 md:p-4"
          >
            <div className="absolute inset-0 bg-brand-primaryDark/80 backdrop-blur-sm" onClick={() => setIsCalcOpen(false)} />
            <motion.div 
              initial={{ y: '100%' }} 
              animate={{ y: 0 }} 
              exit={{ y: '100%' }}
              className="relative w-full max-w-2xl bg-white rounded-t-[32px] md:rounded-[32px] overflow-hidden shadow-2xl h-[90vh] md:h-auto md:max-h-[90vh]"
            >
              <EstimateCalculator isModal onClose={() => setIsCalcOpen(false)} />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Proof Bar */}
      <section className="bg-brand-blush text-brand-darkText py-12 px-4">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div className="p-4 font-bold text-xl">5 to 10x Reach Increase</div>
            <div className="p-4 font-bold text-xl border-y md:border-y-0 md:border-x border-brand-primaryDark/20">16+ Hours Saved Per Week</div>
            <div className="p-4 font-bold text-xl">No Long-Term Contracts</div>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <section className="py-24 px-4 bg-brand-blush/20 text-brand-darkText">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-16 text-brand-primaryDark">Here's How I Can Help</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            <div className="bg-brand-white border-t-8 border-brand-primaryDark rounded-xl p-10 shadow-xl flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-brand-primaryDark">Done-for-You Management</h3>
              <p className="text-gray-700 mb-8 flex-grow text-lg leading-relaxed">
                You focus on your animals. I handle your social media from start to finish. Organic content, paid Meta ads, or both.
              </p>
              <Link to="/services/social-media-management" className="text-brand-mediumPurple font-bold hover:text-brand-primaryDark transition-colors inline-flex items-center text-lg">
                See What's Included <span className="ml-2">→</span>
              </Link>
            </div>
            
            <div className="bg-brand-white border-t-8 border-brand-primaryDark rounded-xl p-10 shadow-xl flex flex-col h-full">
              <h3 className="text-2xl font-bold mb-4 text-brand-primaryDark">1:1 Consulting</h3>
              <p className="text-gray-700 mb-8 flex-grow text-lg leading-relaxed">
                You want to do it yourself but need a real strategy behind it. I'll audit what you have and build you a clear plan.
              </p>
              <Link to="/services/consulting" className="text-brand-mediumPurple font-bold hover:text-brand-primaryDark transition-colors inline-flex items-center text-lg">
                Learn More <span className="ml-2">→</span>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* About Snippet */}
      <section className="py-24 px-4 bg-brand-primaryDark text-brand-white">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16">
          <div className="w-full md:w-5/12">
            <img 
              src={perryImage} 
              alt="Perry Dasilva" 
              className="rounded-2xl shadow-2xl border-4 border-brand-mediumPurple w-full h-auto object-cover aspect-square"
            />
          </div>
          <div className="w-full md:w-7/12">
            <h2 className="text-3xl md:text-4xl font-bold mb-6 text-brand-blush">Hi, I'm Perry</h2>
            <p className="text-lg text-gray-200 mb-6 leading-relaxed">
              I'm a social media strategist based in Laurel, Maryland, and I work specifically with pet care businesses. I help you go from posting randomly to running a strategic presence that actually brings in clients.
            </p>
            <Link to="/about" className="inline-block bg-brand-mediumPurple hover:bg-brand-mauve text-brand-white px-8 py-4 rounded font-bold text-lg transition-colors">
              More About Me →
            </Link>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-4 bg-brand-white">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 text-brand-primaryDark">Here's What Clients Are Saying</h2>
          <SocialJuiceWall />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-4 bg-brand-mediumPurple text-brand-white text-center">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-bold mb-6 leading-tight text-brand-primaryDark">Ready to Stop Posting Into the Void?</h2>
          <a 
            href="https://tidycal.com/digital-by-perry/schedule-a-call" 
            target="_blank" 
            rel="noopener noreferrer"
            className="inline-block bg-brand-primaryDark hover:bg-[#1f2552] text-brand-white px-10 py-5 rounded-lg font-bold text-xl transition-colors shadow-xl"
          >
            Book a Free Discovery Call
          </a>
        </div>
      </section>
    </>
  );
}